import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  Divider,
  Icon,
  InfoOutlined,
  Tooltip,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, func, shape, string } from 'prop-types'

import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { probeDccConnectivity } from 'Pods/ThreeDStudio/Store/actions/dccBridge'

import DccBridgeRow from '../DccBridgeRow/DccBridgeRow'

const REPROBE_INTERVAL_MS = 2000

function DccBridgePopover({ softwares, selectedName = null, onClosePopover }) {
  const [openSubmenuName, setOpenSubmenuName] = useState(null)
  const connectedSoftwares = useThreeDStudioStore(state => state.dccBridge.connectedSoftwares)

  // The hook subscription drives row re-renders; the interval below needs the
  // latest map at tick time but must not retrigger on every state change
  // (that would restart the unconditional initial probe in a feedback loop).
  // A ref kept in sync during render gives both: stable effect identity,
  // fresh reads inside the timer.
  const connectedSoftwaresRef = useRef(connectedSoftwares)
  connectedSoftwaresRef.current = connectedSoftwares

  const { available, comingSoon } = useMemo(() => ({
    available: softwares.filter(s => s.isEnabled),
    comingSoon: softwares.filter(s => !s.isEnabled),
  }), [softwares])

  // Probe each enabled software's local plugin port on mount, then keep
  // re-probing on a short interval *only while a software is reachable* —
  // that way we still detect "plugin just stopped" within one tick, but we
  // don't spam ERR_CONNECTION_REFUSED in the console every interval when no
  // plugin is running. To retry after a known-down state, the user closes
  // and reopens the dropdown, which remounts this effect.
  useEffect(() => {
    available.forEach(s => probeDccConnectivity(s.name))

    const probeReachable = () => {
      const latest = connectedSoftwaresRef.current
      available.forEach(s => {
        if (latest[s.name] !== false) probeDccConnectivity(s.name)
      })
    }
    const id = setInterval(probeReachable, REPROBE_INTERVAL_MS)
    return () => clearInterval(id)
  }, [available])

  const handleSubmenuOpenChange = useCallback((name, open) => {
    setOpenSubmenuName(prev => {
      if (open) return name
      return prev === name ? null : prev
    })
  }, [])

  // Click anywhere in the popover that isn't a three-dot icon (those stop
  // propagation) closes the open submenu. Outside clicks close the whole
  // popover via the parent ClosingDropdown, which unmounts this component
  // anyway.
  const handlePopoverClick = useCallback(() => {
    setOpenSubmenuName(null)
  }, [])

  return (
    <div className="dcc-bridge-popover" onClick={handlePopoverClick} role="presentation">
      <header className="dcc-bridge-popover__header">
        <span className="cgt-body-md-semibold">DCC Bridge</span>
        <Tooltip
          id="dcc-bridge-info-tooltip"
          place="top"
          content="Install and run the plugin first to send models to the corresponding DCC software"
        >
          <Icon
            svgComponent={InfoOutlined}
            color="dark"
            size="small"
            notSpaced
          />
        </Tooltip>
      </header>
      {available.length > 0 && (
        <>
          <Divider customClass="dcc-bridge-popover__divider" />
          <ul className="dcc-bridge-popover__list">
            {available.map(software => (
              <DccBridgeRow
                key={software.name}
                software={software}
                isSelected={software.name === selectedName}
                isSubmenuOpen={openSubmenuName === software.name}
                isConnected={Boolean(connectedSoftwares[software.name])}
                onSubmenuOpenChange={handleSubmenuOpenChange}
                onClosePopover={onClosePopover}
              />
            ))}
          </ul>
        </>
      )}
      {comingSoon.length > 0 && (
        <>
          <Divider customClass="dcc-bridge-popover__divider" />
          <div className="dcc-bridge-popover__caption cgt-caption-semibold">Coming soon</div>
          <ul className="dcc-bridge-popover__list">
            {comingSoon.map(software => (
              <DccBridgeRow
                key={software.name}
                software={software}
                isSubmenuOpen={openSubmenuName === software.name}
                onSubmenuOpenChange={handleSubmenuOpenChange}
                onClosePopover={onClosePopover}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

DccBridgePopover.propTypes = {
  softwares: arrayOf(shape({
    name: string.isRequired,
    iconName: string.isRequired,
    isEnabled: bool.isRequired,
  })).isRequired,
  selectedName: string,
  onClosePopover: func.isRequired,
}

export default memo(DccBridgePopover)
